import { css } from 'emotion';
import { theme } from '@weave/theme';
import type { Direction, Tone, ToneColors } from './entityTagHistory.types';

/* ------------------------ SHARED TOKENS -------------------------------- */

export const toneColors: Record<Tone, ToneColors> = {
  success: {
    solid: theme.colors.success40,
    soft: theme.colors.success5,
    text: theme.colors.success70,
  },
  critical: {
    solid: theme.colors.critical40,
    soft: theme.colors.critical5,
    text: theme.colors.critical70,
  },
  primary: {
    solid: theme.colors.primary40,
    soft: theme.colors.primary5,
    text: theme.colors.primary70,
  },
  explicit: {
    solid: theme.colors.secondary.eggplant50,
    soft: theme.colors.secondary.eggplant5,
    text: theme.colors.secondary.eggplant70,
  },
  neutral: {
    solid: theme.colors.neutral40,
    soft: theme.colors.neutral5,
    text: theme.colors.neutral70,
  },
};

export const TAGGED_COLOR = theme.colors.success40;
export const NOT_TAGGED_COLOR = theme.colors.critical40;
export const UNKNOWN_PATTERN = `repeating-linear-gradient(135deg, ${theme.colors.neutral10} 0 6px, ${theme.colors.neutral5} 6px 12px)`;

export const TOOLTIP_EXPLICIT_GLYPH_COLOR = '#E5AFF3';
export const TOOLTIP_DEFAULT_GLYPH_COLOR = '#8FC0FF';

const directionClipPaths: Record<Direction, string> = {
  up: 'polygon(50% 0, 100% 100%, 0 100%)',
  down: 'polygon(0 0, 100% 0, 50% 100%)',
  none: 'circle(50% at 50% 50%)',
};

// Style fragments reused by several component classes below.
const cardBase = `
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.neutral10};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: 0 1px 2px rgba(32, 35, 40, 0.06);
`;

const eyebrowBase = `
  font-size: ${theme.font.size.small};
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.neutral50};
  margin: 0;
`;

const monoBase = `
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.7rem;
  color: ${theme.colors.neutral50};
`;

/* ------------------------ SHARED -------------------------------- */

export const sharedClasses = {
  mutedText: css`
    color: ${theme.colors.neutral50};
  `,
};

/* ------------------------ ENTITY TAG HISTORY MODAL -------------------------------- */

export const modalClasses = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  toolbar: css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
  `,
  contextChips: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    margin: 0;
  `,
  // `&&` keeps these ahead of the design system's own Text/Button styles.
  formatDescription: css`
    && {
      margin: 0;
      color: ${theme.colors.neutral50};
    }
  `,
  statusMessage: css`
    && {
      margin: 0;
      color: ${theme.colors.neutral60};
    }
  `,
  errorAlert: css`
    ${cardBase}
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(1.5, 2)};
    background: ${theme.colors.critical5};
    border-color: ${theme.colors.critical20};
  `,
  errorMessage: css`
    && {
      margin: 0;
    }
  `,
  actorWarning: css`
    && {
      margin: 0;
      padding: ${theme.spacing(1, 1.5)};
      border-radius: ${theme.borderRadius.medium};
      background: ${theme.colors.warning5};
    }
  `,
  actionButton: css`
    && {
      width: max-content;
    }
  `,
  footer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
};

/* ------------------------ CONTEXT CHIP -------------------------------- */

export const contextChipClasses = {
  root: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.neutral5};
    border: 1px solid ${theme.colors.neutral10};
    max-width: 100%;
  `,
  label: css`
    font-size: ${theme.font.size.small};
    font-weight: 600;
    color: ${theme.colors.neutral50};
  `,
  value: css`
    ${monoBase}
    margin: 0;
    font-size: 0.75rem;
    color: ${theme.colors.neutral90};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};

/* ------------------------ ENTITY TAG HISTORY CONTENT -------------------------------- */

export const contentClasses = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  emptyState: css`
    ${cardBase}
    padding: ${theme.spacing(4)};
    text-align: center;
    color: ${theme.colors.neutral60};
    background: ${theme.colors.neutral5};
    border-style: dashed;
  `,
  tableScroller: css`
    overflow-x: auto;
  `,
};

/* ------------------------ ENUM CELL (EVENT LOG TABLE) -------------------------------- */

export const enumCellClasses = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
  `,
  rawValue: css`
    ${monoBase}
  `,
};

/* ------------------------ SECTION -------------------------------- */

export const sectionClasses = {
  root: css`
    ${cardBase}
    padding: ${theme.spacing(2.5)};
  `,
  header: css`
    margin-bottom: ${theme.spacing(2)};
  `,
  title: css`
    ${eyebrowBase}
    color: ${theme.colors.neutral90};
    font-size: 0.8rem;
  `,
  description: css`
    margin: 4px 0 0;
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
};

/* ------------------------ ENTITY TAG HISTORY SUMMARY -------------------------------- */

export const summaryClasses = {
  grid: css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: ${theme.spacing(1.5)};
  `,
  stateLabel: css`
    transform: scale(1.15);
    transform-origin: left center;
  `,
  lastChangeActor: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
  `,
};

/* ------------------------ SUMMARY CARD / BIG NUMBER / CAPTION -------------------------------- */

export const summaryCardClasses = {
  root: css`
    ${cardBase}
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  label: css`
    ${eyebrowBase}
  `,
  bigNumber: css`
    font-size: ${theme.font.size.h2};
    font-weight: 700;
    line-height: 1.1;
    color: ${theme.colors.neutral90};
  `,
  caption: css`
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
};

/* ------------------------ ENTITY TAG HISTORY TIMELINE -------------------------------- */

export const timelineClasses = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  controls: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
  `,
  chart: css`
    position: relative;
    padding: 44px 8px 0;
  `,
  bar: css`
    position: relative;
    height: 36px;
    border-radius: ${theme.borderRadius.medium};
    background: ${theme.colors.neutral5};
    overflow: hidden;
    box-shadow: inset 0 0 0 1px rgba(32, 35, 40, 0.06);
  `,
  // `left` and `width` are data-driven percentages, so they stay inline on the element.
  segment: (tagged: boolean | undefined, isDimmed: boolean) => css`
    position: absolute;
    top: 0;
    bottom: 0;
    background: ${tagged === undefined
      ? UNKNOWN_PATTERN
      : tagged
      ? TAGGED_COLOR
      : NOT_TAGGED_COLOR};
    opacity: ${isDimmed ? 0.45 : 1};
    transition: opacity 150ms ease, left 300ms ease, width 300ms ease;
  `,
  markers: css`
    position: absolute;
    left: 8px;
    right: 8px;
    top: 40px;
    height: 54px;
  `,
  // `left` is a data-driven percentage, so it stays inline on the element.
  marker: (isActive: boolean, direction: Direction | undefined, tone: Tone) => css`
    position: absolute;
    top: 0;
    height: 100%;
    width: 14px;
    margin-left: -7px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    transition: left 300ms ease;
    z-index: ${isActive ? 2 : 1};

    &::before {
      content: '';
      position: absolute;
      left: 6px;
      top: 0;
      height: 44px;
      width: 2px;
      border-radius: 1px;
      background: ${isActive ? theme.colors.neutral90 : 'rgba(32, 35, 40, 0.5)'};
    }

    &::after {
      content: '';
      position: absolute;
      left: ${isActive ? 0 : 1}px;
      bottom: ${isActive ? -2 : 0}px;
      width: ${isActive ? 14 : 12}px;
      height: ${isActive ? 14 : 12}px;
      clip-path: ${directionClipPaths[direction ?? 'none']};
      background: ${toneColors[tone].solid};
      transition: all 150ms ease;
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary40};
      outline-offset: 2px;
      border-radius: ${theme.borderRadius.small};
    }
  `,
  crowdedNotice: css`
    margin: 0;
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
  crowdedNoticeButton: css`
    border: 0;
    padding: 0;
    background: none;
    color: ${theme.colors.primary50};
    cursor: pointer;
    font-size: inherit;
    text-decoration: underline;
  `,
};

/* ------------------------ TIMELINE TOOLTIP -------------------------------- */

export const timelineTooltipClasses = {
  // `left` is a data-driven percentage, so it stays inline on the element.
  root: (translateX: string) => css`
    position: absolute;
    bottom: calc(100% - 36px);
    transform: translateX(${translateX});
    z-index: 3;
    min-width: 260px;
    max-width: 320px;
    padding: ${theme.spacing(1.5)};
    border-radius: ${theme.borderRadius.medium};
    background: ${theme.colors.neutral90};
    color: ${theme.colors.white};
    box-shadow: ${theme.shadows.floating};
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: ${theme.font.size.small};
  `,
  title: css`
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
  `,
  description: css`
    opacity: 0.75;
  `,
  timestamp: css`
    opacity: 0.8;
  `,
  username: css`
    opacity: 0.75;
  `,
  tagState: (isTagged: boolean) => css`
    color: ${isTagged ? '#7DE3A6' : '#FF9C9C'};
  `,
};

/* ------------------------ TIMELINE AXIS -------------------------------- */

export const timelineAxisClasses = {
  root: css`
    position: relative;
    height: 20px;
    margin-top: 20px;
    border-top: 1px dashed ${theme.colors.neutral20};
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
  // `left` is a data-driven percentage, so it stays inline on the element.
  tick: (isFirst: boolean) => css`
    position: absolute;
    top: 4px;
    white-space: nowrap;
    ${isFirst ? '' : 'transform: translateX(-50%);'}
  `,
  endLabel: css`
    position: absolute;
    top: 4px;
    right: 0;
    font-weight: 600;
  `,
};

/* ------------------------ LEGEND -------------------------------- */

export const legendClasses = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral70};
  `,
  row: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,
  groupLabel: css`
    font-weight: 700;
    color: ${theme.colors.neutral90};
  `,
  item: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
  `,
  swatch: (background: string) => css`
    width: 18px;
    height: 10px;
    border-radius: 3px;
    background: ${background};
  `,
};

/* ------------------------ SEGMENTED CONTROL -------------------------------- */

export const segmentedControlClasses = {
  root: css`
    display: inline-flex;
    padding: 3px;
    gap: 2px;
    border-radius: ${theme.borderRadius.medium};
    background: ${theme.colors.neutral5};
    border: 1px solid ${theme.colors.neutral10};
  `,
  option: (isSelected: boolean) => css`
    border: 0;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: ${theme.font.size.small};
    font-weight: 600;
    background: ${isSelected ? theme.colors.white : 'transparent'};
    color: ${isSelected ? theme.colors.neutral90 : theme.colors.neutral60};
    box-shadow: ${isSelected ? '0 1px 3px rgba(32, 35, 40, 0.15)' : 'none'};
    transition: all 150ms ease;

    &:hover {
      color: ${theme.colors.neutral90};
    }

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary40};
      outline-offset: 1px;
    }
  `,
};

/* ------------------------ DIRECTION GLYPH -------------------------------- */

export const directionGlyphClasses = {
  root: (direction: Direction, color: string, size: number) => css`
    display: inline-block;
    width: ${size}px;
    height: ${size}px;
    background: ${color};
    clip-path: ${directionClipPaths[direction]};
    flex-shrink: 0;
  `,
};

/* ------------------------ PILL -------------------------------- */

export const pillClasses = {
  root: (tone: Tone, hasTitle: boolean) => css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: ${theme.borderRadius.full};
    background: ${toneColors[tone].soft};
    color: ${toneColors[tone].text};
    border: 1px solid ${toneColors[tone].solid}33;
    font-size: ${theme.font.size.small};
    font-weight: 600;
    white-space: nowrap;
    width: fit-content;
    ${hasTitle ? 'cursor: pointer;' : ''}
  `,
  dot: (tone: Tone) => css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${toneColors[tone].solid};
    flex-shrink: 0;
  `,
};

/* ------------------------ ACTOR LABEL -------------------------------- */

export const actorLabelClasses = {
  root: css`
    display: inline-grid;
    align-items: center;
    gap: 8px;
    min-width: 0;
    grid-auto-flow: column;
    cursor: pointer;
  `,
  avatar: (hasName: boolean) => css`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${hasName ? theme.colors.primary10 : theme.colors.neutral10};
    color: ${hasName ? theme.colors.primary70 : theme.colors.neutral60};
    display: ${hasName ? 'inline-flex' : 'none'};
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    flex-shrink: 0;
  `,
  name: (hasName: boolean) => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${hasName ? '' : monoBase}
  `,
};

/* ------------------------ EVENT TIMESTAMP -------------------------------- */

export const eventTimestampClasses = {
  root: css`
    display: flex;
    flex-direction: column;
    line-height: 1.3;
  `,
  date: css`
    font-weight: 600;
  `,
  time: css`
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
};
